import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AdSyncConfigDto,
  SubmissionDeadlineDto,
  SubmissionDeadlineResponse,
  AdSyncStartedDto,
  AddTeamMemberRequest,
  AdminCategoryAudienceImpact,
  AdminCategoryItem,
  CategoryVisibility,
  CreateAdminCategoryRequest,
  UpdateAdminCategoryRequest,
  AdminTeamDto,
  AdminTeamMembersResponse,
  AdminTeamsResponse,
  AdminUserLinesResponse,
  AdminUserMembershipsResponse,
  AdminUsersResponse,
  AuditLogResponse,
  AdminReportingPeriodsResponse,
  CreateReportingPeriodRequest,
  CreateTeamRequest,
  PatchAdminUserHourTargetRequest,
  PatchAdminUserSystemStartDateRequest,
  PatchReportingPeriodRequest,
  PeriodUnresolvedResponse,
  ReportingPeriodDto,
  UpdateAdSyncConfigRequest,
  UpdateTeamMemberRoleRequest,
  UpdateTeamRequest,
  UpdateUserActiveRequest,
  UpdateUserRoleRequest,
} from '@time-tracking/shared';

import { parseJson } from './client.js';
import { generalActivitiesKeys } from './general-activities.js';
import { useApiFetch } from './useApiFetch.js';

export function useAdminTeams(options?: { enabled?: boolean }) {
  const fetch = useApiFetch();
  return useQuery<AdminTeamsResponse>({
    queryKey: ['admin', 'teams'],
    queryFn: () => fetch('/v1/admin/teams').then((r) => parseJson<AdminTeamsResponse>(r)),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateTeam() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<AdminTeamDto, Error, CreateTeamRequest>({
    mutationFn: (data) =>
      fetch('/v1/admin/teams', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<AdminTeamDto>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

export function useUpdateTeam() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<AdminTeamDto, Error, { teamId: string } & UpdateTeamRequest>({
    mutationFn: ({ teamId, ...data }) =>
      fetch(`/v1/admin/teams/${teamId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }).then((r) => parseJson<AdminTeamDto>(r)),
    onSuccess: (_data, { teamId }) => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams', teamId, 'members'] });
    },
  });
}

export function useDeleteTeam() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: async (teamId) => {
      const r = await fetch(`/v1/admin/teams/${teamId}`, { method: 'DELETE' });
      // The API returns 404 for an already-deleted team so it never writes a
      // TEAM_DELETED for an operation that destroyed nothing. From here the
      // caller's intent is satisfied either way, so converge instead of erroring
      // on a stale list.
      if (r.status === 404) return;
      if (!r.ok) await parseJson<never>(r);
    },
    // Settled, not success: a failed delete may still have changed the server
    // (or revealed the list is stale), and the old list must not linger.
    //
    // The whole cache, not just the team list. Deleting a team removes its
    // project and task links, deactivates the assignments that fanned out from
    // its membership, drops the now-empty draft timesheet lines, and changes
    // members' derived roles — all denormalised into project, task-assignment,
    // timesheet, admin-user and audit views. With a 30s staleTime those keep
    // serving data the delete removed. Same reasoning as the whole-roster AD
    // sync in UsersPane: this is rare and by hand, so dropping the cache is
    // cheaper than enumerating every affected key and missing one.
    onSettled: () => {
      void queryClient.invalidateQueries();
    },
  });
}

export function useAssignToTeam() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { teamId: string; userId: string }>({
    mutationFn: async ({ teamId, userId }) => {
      const r = await fetch(`/v1/admin/teams/${teamId}/members`, {
        method: 'POST',
        body: JSON.stringify({ userId, role: 'contributor' }),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
      // Prefix, not ['approvals','inbox']: TanStack matches by key prefix, so the
      // narrower key does not reach ['approvals','outstanding', ...] and an
      // assigned row would sit there reading "No approver of record" until refocus.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

export function useAdminTeamMembers(teamId: string | null) {
  const fetch = useApiFetch();
  return useQuery<AdminTeamMembersResponse>({
    queryKey: ['admin', 'teams', teamId, 'members'],
    queryFn: () => {
      const id = teamId as string;
      return fetch(`/v1/admin/teams/${id}/members`).then((r) =>
        parseJson<AdminTeamMembersResponse>(r),
      );
    },
    enabled: teamId !== null,
  });
}

export function useAddTeamMember(teamId: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, AddTeamMemberRequest>({
    mutationFn: async (data) => {
      const r = await fetch(`/v1/admin/teams/${teamId}/members`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams', teamId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

export function useRemoveTeamMember(teamId: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: async (userId) => {
      const r = await fetch(`/v1/admin/teams/${teamId}/members/${userId}`, { method: 'DELETE' });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams', teamId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

export function useUpdateTeamMemberRole(teamId: string) {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { userId: string } & UpdateTeamMemberRoleRequest>({
    mutationFn: async ({ userId, ...data }) => {
      const r = await fetch(`/v1/admin/teams/${teamId}/members/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams', teamId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'teams'] });
    },
  });
}

export function useAdminUsers() {
  const fetch = useApiFetch();
  return useQuery<AdminUsersResponse>({
    queryKey: ['admin', 'users'],
    queryFn: () => fetch('/v1/admin/users').then((r) => parseJson<AdminUsersResponse>(r)),
  });
}

export function useAdminUserMemberships(userId: string | null) {
  const fetch = useApiFetch();
  return useQuery<AdminUserMembershipsResponse>({
    queryKey: ['admin', 'users', userId, 'memberships'],
    queryFn: () => {
      const id = userId as string;
      return fetch(`/v1/admin/users/${id}/memberships`).then((r) =>
        parseJson<AdminUserMembershipsResponse>(r),
      );
    },
    enabled: userId !== null,
  });
}

export function useAdminUserLines(userId: string | null) {
  const fetch = useApiFetch();
  return useQuery<AdminUserLinesResponse>({
    queryKey: ['admin', 'users', userId, 'lines'],
    queryFn: () => {
      const id = userId as string;
      return fetch(`/v1/admin/users/${id}/lines`).then((r) => parseJson<AdminUserLinesResponse>(r));
    },
    enabled: userId !== null,
  });
}

export function useUpdateUserRole() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { userId: string } & UpdateUserRoleRequest>({
    mutationFn: async ({ userId, ...data }) => {
      const r = await fetch(`/v1/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
    },
  });
}

export function useSetUserActive() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { userId: string } & UpdateUserActiveRequest>({
    mutationFn: async ({ userId, ...data }) => {
      const r = await fetch(`/v1/admin/users/${userId}/active`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] });
      // active_flag filters the admin-scope approvals roster, so refresh it too —
      // otherwise it stays stale after a deactivate/reactivate. The whole `approvals`
      // prefix, not `['approvals','inbox']`: that narrower key does not prefix-match the
      // cross-week aggregate (`['approvals','outstanding',…]`) or the drawer detail
      // (`['approvals','item',…]`), so those two kept serving a roster this field had
      // already changed. R26 asks every mutation to reach all three.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

export function useUpdateUserHourTarget() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { userId: string } & PatchAdminUserHourTargetRequest>({
    mutationFn: async ({ userId, ...data }) => {
      const r = await fetch(`/v1/admin/users/${userId}/hour-target`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      // A changed hour target affects every cached week's submit threshold, so
      // invalidate all ['timesheet', *] entries, not just one week.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
      // The whole prefix: the target is also what the triage view measures under-hours
      // anomalies against, and it appears in the drawer detail, neither of which
      // `['approvals','inbox']` reaches.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

export function useUpdateUserSystemStartDate() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, { userId: string } & PatchAdminUserSystemStartDateRequest>({
    mutationFn: async ({ userId, ...data }) => {
      const r = await fetch(`/v1/admin/users/${userId}/system-start-date`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      // The start date is the floor on how far back outstanding weeks are
      // scanned, so every cached week and the approvals inbox can shift with it
      // — same blast radius as the hour target.
      void queryClient.invalidateQueries({ queryKey: ['timesheet'] });
      // The whole prefix. This one matters most of the three: the start date IS the
      // floor on the cross-week scan, so changing it adds or removes whole weeks from
      // the aggregate — the surface `['approvals','inbox']` cannot reach.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
      // The nudge reads that same floor. Without this, an administrator editing
      // their *own* start date leaves their back-arrow badge showing a count
      // computed against the old floor until something else triggers a refetch.
      // Prefix, not the fully-qualified key, so it matches whichever Monday is
      // cached (see useOutstandingWeeks).
      void queryClient.invalidateQueries({ queryKey: ['outstanding-weeks'] });
    },
  });
}

/**
 * `pollMs` turns on background refetching while a sync is running. "Sync now"
 * returns as soon as the run is accepted, so this query is how the outcome
 * arrives.
 */
export function useAdSyncConfig(pollMs?: number | false) {
  const fetch = useApiFetch();
  return useQuery<AdSyncConfigDto>({
    queryKey: ['admin', 'ad-sync', 'config'],
    queryFn: () => fetch('/v1/admin/ad-sync/config').then((r) => parseJson<AdSyncConfigDto>(r)),
    refetchInterval: pollMs ?? false,
  });
}

/**
 * When a week's hours are due.
 *
 * The GET is open to any authenticated user — the approvals footnote states the rule so
 * the people it labels late can read it — while only administrators may PUT. This hook
 * pair backs the administrator's control; the footnote reads the value off the
 * outstanding-approvals response it already has.
 */
export function useSubmissionDeadline() {
  const fetch = useApiFetch();
  return useQuery<SubmissionDeadlineDto>({
    queryKey: ['admin', 'submission-deadline'],
    queryFn: () =>
      fetch('/v1/submission-deadline')
        .then((r) => parseJson<SubmissionDeadlineResponse>(r))
        .then((b) => b.deadline),
  });
}

export function useUpdateSubmissionDeadline() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  // Returns the deadline the server echoes back, not the one that was sent: the cache
  // should hold what was actually stored.
  return useMutation<SubmissionDeadlineDto, Error, SubmissionDeadlineDto>({
    mutationFn: async (data) => {
      const r = await fetch('/v1/admin/submission-deadline', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return (await parseJson<SubmissionDeadlineResponse>(r)).deadline;
    },
    onSuccess: (deadline) => {
      // Written before the invalidation, not left to it. `invalidateQueries` marks the
      // query stale and starts a refetch, but the cache still holds the OLD value until
      // that lands — and the card drops its local edit the moment this succeeds, so it
      // re-renders the value it just replaced. The visible result is a save that appears
      // to do nothing, and if the refetch then fails the card sits there confirming
      // "Saved. Hours are due <the old rule>", which is a false statement about a
      // setting whose whole job is telling people what they are late against.
      queryClient.setQueryData(['admin', 'submission-deadline'], deadline);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'submission-deadline'] });
      // Every dueAt and daysPastDue in the triage view is computed against this rule, so
      // changing it retroactively relabels how late everyone is. Without this the
      // Approvals view keeps showing lateness measured against the old deadline until
      // something else happens to refetch it.
      void queryClient.invalidateQueries({ queryKey: ['approvals'] });
    },
  });
}

export function useUpdateAdSyncConfig() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, UpdateAdSyncConfigRequest>({
    mutationFn: async (data) => {
      const r = await fetch('/v1/admin/ad-sync/config', {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'ad-sync', 'config'] });
    },
  });
}

/**
 * Start a sync. Resolves once the run has been accepted, not once it has
 * finished — the API returns 202 because a large enough roster outruns API
 * Gateway's 29s integration ceiling. Poll `lastSync` for a result carrying the
 * resolved `runId`; matching on the id rather than on "lastSync changed" is what
 * keeps a concurrent scheduled run from being mistaken for this one.
 */
export function useAdSyncNow() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<AdSyncStartedDto, Error, undefined>({
    mutationFn: () =>
      fetch('/v1/admin/ad-sync', { method: 'POST' }).then((r) => parseJson<AdSyncStartedDto>(r)),
    onError: () => {
      // Only a failure to *start* reaches here; a run that starts and then fails
      // records its own errors in lastSync. Write a synthetic result so the
      // failure survives tab navigation (mutation state resets on unmount).
      queryClient.setQueryData<AdSyncConfigDto>(['admin', 'ad-sync', 'config'], (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          lastSync: {
            ranAt: new Date().toISOString(),
            total: 0,
            added: 0,
            updated: 0,
            deactivated: 0,
            durationMs: 0,
            errors: ['Sync could not be started — check CloudWatch logs'],
          },
        };
      });
    },
  });
}

export function useReportingPeriods() {
  const fetch = useApiFetch();
  return useQuery<AdminReportingPeriodsResponse>({
    queryKey: ['admin', 'reporting-periods'],
    queryFn: () =>
      fetch('/v1/admin/reporting-periods').then((r) => parseJson<AdminReportingPeriodsResponse>(r)),
  });
}

export function useCreateReportingPeriod() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ReportingPeriodDto, Error, CreateReportingPeriodRequest>({
    mutationFn: (data) =>
      fetch('/v1/admin/reporting-periods', {
        method: 'POST',
        body: JSON.stringify(data),
      }).then((r) => parseJson<ReportingPeriodDto>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reporting-periods'] });
    },
  });
}

export function useUpdateReportingPeriodStatus() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<ReportingPeriodDto, Error, { periodId: string } & PatchReportingPeriodRequest>(
    {
      mutationFn: ({ periodId, ...data }) =>
        fetch(`/v1/admin/reporting-periods/${periodId}`, {
          method: 'PATCH',
          body: JSON.stringify(data),
        }).then((r) => parseJson<ReportingPeriodDto>(r)),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['admin', 'reporting-periods'] });
      },
    },
  );
}

export function useDeleteReportingPeriod() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<undefined, Error, string>({
    mutationFn: async (periodId) => {
      const r = await fetch(`/v1/admin/reporting-periods/${periodId}`, { method: 'DELETE' });
      if (!r.ok) await parseJson<never>(r);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'reporting-periods'] });
    },
  });
}

export function useUnresolvedForPeriod(periodId: string | null) {
  const fetch = useApiFetch();
  return useQuery<PeriodUnresolvedResponse>({
    queryKey: ['admin', 'reporting-periods', periodId, 'unresolved'],
    queryFn: () =>
      fetch(`/v1/admin/reporting-periods/${periodId}/unresolved`).then((r) =>
        parseJson<PeriodUnresolvedResponse>(r),
      ),
    enabled: periodId !== null,
    staleTime: 30_000,
  });
}

export interface AuditLogFilters {
  limit?: number;
  offset?: number;
  actor?: string;
  objectType?: string;
  range?: '24h' | '7d' | '30d' | 'all';
  search?: string;
}

// ── Non-project categories (admin) ──────────────────────────────────────────

// Single source of truth for this query lives in api/hooks.ts (also consumed by
// the Timesheet). Re-export it so the admin pane can pull the query + the
// mutations below from one module, without a second hook that could drift.
export { useAdminCategories } from './hooks.js';

// Same key the re-exported hook uses, so the mutations below invalidate its
// cache entry.
const ADMIN_CATEGORIES_KEY = ['admin-categories'] as const;

export function useCreateCategory() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<AdminCategoryItem, Error, CreateAdminCategoryRequest>({
    mutationFn: (data) =>
      fetch('/v1/admin-categories', { method: 'POST', body: JSON.stringify(data) }).then((r) =>
        parseJson<AdminCategoryItem>(r),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_CATEGORIES_KEY });
      // A category created as, switched into or out of "Assigned by leads" —
      // or renamed, retyped, deactivated — changes the General activities grid.
      void queryClient.invalidateQueries({ queryKey: generalActivitiesKeys.all });
    },
  });
}

export function useUpdateCategory() {
  const fetch = useApiFetch();
  const queryClient = useQueryClient();
  return useMutation<
    AdminCategoryItem,
    Error,
    { categoryId: string; patch: UpdateAdminCategoryRequest }
  >({
    mutationFn: ({ categoryId, patch }) =>
      fetch(`/v1/admin-categories/${categoryId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }).then((r) => parseJson<AdminCategoryItem>(r)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_CATEGORIES_KEY });
      // A category created as, switched into or out of "Assigned by leads" —
      // or renamed, retyped, deactivated — changes the General activities grid.
      void queryClient.invalidateQueries({ queryKey: generalActivitiesKeys.all });
    },
  });
}

/**
 * Who switching a category to visibility `to` would affect, split by outcome,
 * for the switch-confirm dialog.
 *
 * Fetched per dialog open — `categoryId` is null until one is pending — and
 * never cached across opens. A count quoted in a confirmation has to describe
 * the database as it is now, not as it was the last time this pane rendered:
 * the pane's other data tolerates a stale window because a stale row only looks
 * wrong, whereas a stale count here is the evidence the decision is made on.
 * `staleTime: 0` + `gcTime: 0` is the cheap way to say that, and the cost is one
 * admin-triggered query. Fetched once per open, though: no refetch on window
 * focus or reconnect, which with `staleTime: 0` would re-run the preview (and
 * flash the dialog's loading state) under an admin reading it. Retry still
 * refetches on demand.
 */
export function useCategoryAudienceImpact(categoryId: string | null, to: CategoryVisibility) {
  const fetch = useApiFetch();
  return useQuery<AdminCategoryAudienceImpact>({
    queryKey: ['admin', 'admin-categories', categoryId, 'audience-impact', to],
    queryFn: () => {
      const id = categoryId as string;
      const params = new URLSearchParams({ to });
      return fetch(`/v1/admin-categories/${id}/audience-impact?${params.toString()}`).then((r) =>
        parseJson<AdminCategoryAudienceImpact>(r),
      );
    },
    enabled: categoryId !== null,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useAuditLog(filters: AuditLogFilters) {
  const fetch = useApiFetch();
  return useQuery<AuditLogResponse>({
    queryKey: ['admin', 'audit', filters],
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.limit !== undefined) params.set('limit', String(filters.limit));
      if (filters.offset !== undefined) params.set('offset', String(filters.offset));
      if (filters.actor) params.set('actor', filters.actor);
      if (filters.objectType) params.set('objectType', filters.objectType);
      if (filters.range) params.set('range', filters.range);
      if (filters.search) params.set('search', filters.search);
      const qs = params.toString();
      return fetch(`/v1/admin/audit${qs ? `?${qs}` : ''}`).then((r) =>
        parseJson<AuditLogResponse>(r),
      );
    },
  });
}
